import { environment } from 'environments/environment';

/**
 * Sanitizes log arguments by removing newline characters from strings.
 * This helps prevent log injection vulnerabilities and ensures logs are single-line.
 * @param args Array of arguments to sanitize
 * @returns Sanitized arguments array
 */
function sanitizeLogArgs(args: unknown[]): unknown[] {
  return args.map(arg => {
    if (typeof arg === 'string') {
      return arg.replace(/[\r\n]/g, ' '); // Remove newlines
    }
    return arg;
  });
}

/**
 * DebugLogger provides static methods for logging in development and test environments.
 * - Logs are disabled in production for security and performance.
 * - All log inputs are sanitized to mitigate log injection risks.
 */
export class DebugLogger {
  /**
   * Logs messages to the console in non-production environments.
   * @param args Arguments to log
   */
  static log(...args: unknown[]): void {
    if (!environment.production) {
      console.info(...sanitizeLogArgs(args));
    }
  }

  /**
   * Logs warnings to the console in non-production environments.
   * @param args Arguments to log as warnings
   */
  static warn(...args: unknown[]): void {
    if (!environment.production) {
      console.warn(...sanitizeLogArgs(args));
    }
  }

  /**
   * Logs errors to the console in non-production environments.
   * @param args Arguments to log as errors
   */
  static error(...args: unknown[]): void {
    if (!environment.production) {
      console.assert(false, ...sanitizeLogArgs(args));
    }
  }

  /**
   * Logs a debug section message in non-production environments.
   * Used to visually separate debug output in the console.
   * @param message Optional section message
   */
  static section(message?: string): void {
    if (!environment.production) {
      const sanitizedMessage = (message && String(sanitizeLogArgs([message])[0])) || '';
      console.group(
        `[DEBUG] ${sanitizedMessage} (These logs are only visible in DEV/TEST, not in PROD. Go worry for something else!)`
      );
      console.groupEnd();
    }
  }
}
